import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventMudChecksQuery } from "@api/pApi";
import { fmtMoney, fmtNum } from "@lib/format";
import type { MudProductOut, MudProductTranOut } from "@api/types";
import type { Locale } from "@i18n/locales";

/** Sum a numeric field across transaction rows, guarding nulls. */
function sumField(rows: MudProductTranOut[], pick: (t: MudProductTranOut) => number | null): number {
  return rows.reduce((s, t) => {
    const v = pick(t);
    return s + (v != null && Number.isFinite(v) ? v : 0);
  }, 0);
}

/** Format an ISO date (date_first_used) in the tenant locale, or em-dash. */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Mud & fluids — the event's mud product inventory (event-scoped) plus its full
 * product-transaction trend, both served by Oxbo-P via /events/{id}/mud-checks.
 * Every number is server-supplied; the FE only formats and lays out (the KPI
 * band's simple totals are display sums over the served rows, not recomputed
 * KPIs). Mirrors the MudChecksResponse contract: products + transactions only
 * (volumes live on the per-day report, not on the event trend).
 */
export function MudPanel({ eventId, reportId }: { eventId: string; reportId?: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventMudChecksQuery(eventId, {
    skip: !eventId,
  });

  // Guard against a non-array payload (defensive — the contract defaults these
  // to []; a malformed body shouldn't crash the screen).
  const products: MudProductOut[] = Array.isArray(data?.products) ? data!.products : [];
  const allTransactions: MudProductTranOut[] = Array.isArray(data?.transactions)
    ? data!.transactions
    : [];
  // On a Daily Report (reportId set) the mud trend is scoped to THAT day; on the
  // standalone event Mud view it shows every day, grouped by report_date (mirrors
  // the Time Log's NptPanel). Each transaction carries report_date + daily_report_id
  // from the P route's LEFT-JOIN on daily_report.
  const transactions: MudProductTranOut[] = reportId
    ? allTransactions.filter((x) => x.daily_report_id === reportId)
    : allTransactions;
  const groupByDay = !reportId;

  const stats: Stat[] = data
    ? [
        { label: t("mud.kpi.products"), value: fmtNum(products.length, locale) },
        {
          label: t("mud.kpi.received"),
          value: fmtNum(sumField(transactions, (x) => x.quantity_received), locale, {
            maximumFractionDigits: 1,
          }),
        },
        {
          label: t("mud.kpi.used"),
          value: fmtNum(sumField(transactions, (x) => x.quantity_used), locale, {
            maximumFractionDigits: 1,
          }),
          tone: "ops",
        },
        {
          label: t("mud.kpi.cost"),
          value: fmtMoney(sumField(transactions, (x) => x.product_daily_cost), locale, {
            compact: true,
          }),
        },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && products.length === 0 && transactions.length === 0}
      emptyMessage={t("mud.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("mud.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      {/* Product inventory (event-scoped) */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("mud.products")}</h3>
        {products.length === 0 ? (
          <div className="empty-state">{t("mud.products.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("mud.col.product")}</th>
                  <th>{t("mud.col.vendor")}</th>
                  <th className="num">{t("mud.col.unitPrice")}</th>
                  <th className="num">{t("mud.col.unitSize")}</th>
                  <th>{t("mud.col.unitMeasure")}</th>
                  <th>{t("mud.col.firstUsed")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-owner="p">
                    <td>{p.product_name ?? "—"}</td>
                    <td>{p.vendor_entry_key ?? "—"}</td>
                    <td className="num tab-nums">
                      {p.unit_price != null
                        ? fmtMoney(p.unit_price, locale, { currency: p.currency ?? undefined })
                        : "—"}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(p.unit_size, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td>{p.unit_measure_entry_key ?? "—"}</td>
                    <td>{fmtDate(p.date_first_used, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product-transaction trend */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("mud.transactions")}</h3>
        {transactions.length === 0 ? (
          <div className="empty-state">{t("mud.transactions.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("mud.col.received")}</th>
                  <th className="num">{t("mud.col.used")}</th>
                  <th className="num">{t("mud.col.adjusted")}</th>
                  <th className="num">{t("mud.col.returned")}</th>
                  <th className="num">{t("mud.col.dailyCost")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.flatMap((tr, i) => {
                  const prev = transactions[i - 1];
                  const dayHeader =
                    groupByDay && tr.report_date && tr.report_date !== prev?.report_date ? (
                      <tr key={`day-${tr.report_date}-${tr.id}`} className="day-group-row">
                        <td colSpan={5} className="day-group">
                          {new Date(`${tr.report_date}T00:00:00`).toLocaleDateString(locale, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                      </tr>
                    ) : null;
                  const row = (
                    <tr key={tr.id} data-owner="p">
                      <td className="num tab-nums">
                        {fmtNum(tr.quantity_received, locale, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="num tab-nums">
                        {fmtNum(tr.quantity_used, locale, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="num tab-nums">
                        {fmtNum(tr.quantity_adjusted, locale, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="num tab-nums">
                        {fmtNum(tr.quantity_returned, locale, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="num tab-nums">
                        {tr.product_daily_cost != null
                          ? fmtMoney(tr.product_daily_cost, locale, {
                              currency: tr.currency ?? undefined,
                            })
                          : "—"}
                      </td>
                    </tr>
                  );
                  return dayHeader ? [dayHeader, row] : [row];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryState>
  );
}
